import { createWebHistory, createRouter } from "vue-router";
import routes from './routes';
import appConfig from "../../app.config";
import store from '@/state/store';
import {
  ROLE_ADMIN,
  confinedRedirect,
  confinesOnPublicRoutes,
} from '@/constants/roles';

const router = createRouter({
  history: createWebHistory(),
  routes,
  // Use the HTML5 history API (i.e. normal-looking routes)
  // instead of routes with hashes (e.g. example.com/#/about).
  // This may require some server configuration in production:
  // https://router.vuejs.org/en/essentials/history-mode.html#example-server-configurations
  mode: 'history',
  // Simulate native-like scroll behavior when navigating to a new
  // route and using back/forward buttons.
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    } else {
      return { top: 0, left: 0 };
    }
  },
});

// Authentication guard
// Checks if user is authenticated before accessing protected routes
router.beforeEach(async (to, from, next) => {
  const authRequired = to.matched.some((route) => route.meta.authRequired);
  const accessToken = store.state.auth.accessToken;

  // If user is already logged in and trying to access login page, redirect to home.
  // Confined roles will then be redirected from "/" to their own area below.
  if (!authRequired && to.path === '/login' && store.getters['auth/loggedIn']) {
    return next('/');
  }

  // Public pages remain public for anonymous visitors. An authenticated visitor must
  // continue through the role confinement check, including on /demo and TV screens.
  if (!authRequired && !accessToken) return next();
  if (!accessToken) {
    return next({
      path: '/login',
      query: { redirect: to.fullPath }
    });
  }

  // Token exists - validate and fetch user if not already loaded
  if (!store.state.auth.currentUser) {
    try {
      await store.dispatch('auth/fetchCurrentUser');
    } catch (error) {
      // A stale token must not turn a public page into a protected page. Protected
      // routes keep the existing behavior and return to login.
      if (!authRequired) return next();
      return next({
        path: '/login',
        query: { redirect: to.fullPath }
      });
    }
  }

  const roleName = store.state.auth.currentUser?.role_name;

  // Route công khai: chỉ role có `confinePublicRoutes` (hiện là quản đốc) mới phải
  // đi tiếp xuống `confinedRedirect` bên dưới. Kế toán/thủ kho giữ nguyên hành vi cũ,
  // xem được các trang công khai (TV xưởng, trang demo khách) như trước khi có role
  // quản đốc — không âm thầm bị đá theo luật khoá vùng mới thêm cho role khác.
  if (!authRequired && !confinesOnPublicRoutes(roleName)) return next();

  // Role bị khoá trong đúng MỘT khu vực: kế toán → /accounting/*. Mọi route
  // ngoài khu vực đó đá về trang chủ của chính họ.
  //
  // Đặt TRƯỚC khối requiresAdmin bên dưới để tránh vòng lặp redirect giữa hai khối
  // (nếu đặt sau: role bị requiresAdmin đá về "/" trước, rồi guard chạy lại từ đầu
  // cho "/", khối này lại đá về trang chủ khu vực — vẫn dừng đúng 1 vòng nhờ thứ tự
  // hiện tại, nhưng nếu đảo ngược thứ tự hai khối sẽ tạo vòng lặp).
  //
  // Luật khoá vùng nằm trong `confinedRedirect` (constants/roles.cjs, có test): chép
  // khối `if` này thêm một lần cho mỗi role là chỗ dễ sai nhất — quên một dấu `!` là
  // vòng lặp redirect, triệu chứng chỉ là trang trắng và không chỉ về dòng code nào cả.
  //
  // Hàm đó cũng giữ danh sách route AI CŨNG VÀO ĐƯỢC (/pages/profile-setting, /logout):
  // thiếu nó thì kế toán không đổi được mật khẩu của chính mình, vì hai đường dẫn đó
  // không nằm trong /accounting/.
  const redirectTo = confinedRedirect(roleName, to.path);
  if (redirectTo) {
    return next(redirectTo);
  }

  // Authenticated users without a confined role can now enter the public route.
  if (!authRequired) return next();

  // Route chỉ dành cho admin (tổ may / quản lý & thống kê nhân viên / tồn đầu kỳ).
  const requiresAdmin = to.matched.some((route) => route.meta.requiresAdmin);
  if (requiresAdmin && roleName !== ROLE_ADMIN) {
    return next('/');
  }

  next();
});

router.beforeResolve(async (routeTo, routeFrom, next) => {
  // Create a `beforeResolve` hook, which fires whenever
  // `beforeRouteEnter` and `beforeRouteUpdate` would. This
  // allows us to ensure data is fetched even when params change,
  // but the resolved route does not. We put it in `meta` to
  // indicate that it's a hook we created, rather than part of
  // Vue Router (yet?).
  try {
    // For each matched route...
    for (const route of routeTo.matched) {
      await new Promise((resolve, reject) => {
        // If a `beforeResolve` hook is defined, call it with
        // the same arguments as the `beforeEnter` hook.
        if (route.meta && route.meta.beforeResolve) {
          route.meta.beforeResolve(routeTo, routeFrom, (...args) => {
            // If the user chose to redirect...
            if (args.length) {
              // If redirecting to the same route we're coming from...
              // Complete the redirect.
              next(...args);
              reject(new Error('Redirected'));
            } else {
              resolve();
            }
          });
        } else {
          // Otherwise, continue resolving the route.
          resolve();
        }
      });
    }
    // If a `beforeResolve` hook chose to redirect, just return.
  } catch (error) {
    return;
  }
  document.title = routeTo.meta.title + ' | ' + appConfig.title;
  // If we reach this point, continue resolving the route.
  next();
});

export default router;
